// components/BlogContent.js
"use client";

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import Button from './button';

const calculateWordCount = (content) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    const text = div.textContent || '';
    return text.split(/\s+/).filter(word => word.length > 0).length;
};

const BlogContent = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('Tous');

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/api/articles`, {
                    credentials: 'include'
                });
                const data = await res.json();
                const articlesWithReadingTime = data.map(article => {
                    const wordCount = calculateWordCount(article.content);
                    const readingTime = Math.ceil(wordCount / 200);
                    return {
                        ...article,
                        readingTime
                    };
                });
                setArticles(articlesWithReadingTime);
                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération des articles :", error);
                setLoading(false);
            }
        };

        fetchArticles();
    }, []);

    if (loading) {
        return <p>Chargement des articles...</p>;
    }

    const filteredArticles = articles.filter(article =>
        article.published && (category === 'Tous' || article.category === category)
    );

    const truncateDescription = (description) => {
        if (description.length > 250) {
            return `${description.substring(0, 250)}...`;
        }
        return description;
    };

    const getBadgeStyle = (category) => {
        switch (category) {
            case 'Fertilité':
                return {
                    gradient: 'from-emerald-600 via-emerald-500 to-teal-500',
                    icon: '🌱',
                    shadow: 'shadow-emerald-200'
                };
            case 'Perte de poids':
                return {
                    gradient: 'from-emerald-500 via-teal-400 to-cyan-400',
                    icon: '⚖️',
                    shadow: 'shadow-emerald-200'
                };
            case 'Troubles hormonaux':
                return {
                    gradient: 'from-slate-600 via-slate-500 to-gray-500',
                    icon: '⚖️',
                    shadow: 'shadow-slate-200'
                };
            default:
                return {
                    gradient: 'from-gray-500 via-gray-400 to-slate-400',
                    icon: '📝',
                    shadow: 'shadow-gray-200'
                };
        }
    };

    return (
        <>
            <div className="container mx-auto p-6">
                {/* Filtre de catégories */}
                <div className="hidden sm:flex justify-center mb-8">
                    {['Tous', 'Fertilité', 'Perte de poids', 'Troubles hormonaux'].map(cat => (
                        <button
                            key={cat}
                            onClick={() => setCategory(cat)}
                            className={`px-4 py-2 mx-2 rounded-full ${category === cat ? 'bg-[#2C6E63] text-white' : 'bg-white text-black'} border border-[#2C6E63] transition-all duration-200`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="sm:hidden mb-8">
                    <select
                        onChange={(e) => setCategory(e.target.value)}
                        className="block w-full p-2 border border-[#2C6E63] rounded-md bg-white text-black"
                        value={category}
                    >
                        {['Tous', 'Fertilité', 'Perte de poids', 'Troubles hormonaux'].map(cat => (
                            <option key={cat} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Liste des articles */}
                <h2 className="sr-only">Liste des articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredArticles.map((article) => {
                        const { gradient, icon, shadow } = getBadgeStyle(article.category);
                        return (
                            <div key={article._id} className="article-card bg-white border border-[#D4DDD1] shadow-md rounded-3xl p-6 relative transition-transform transform hover:scale-[1.02] hover:shadow-2xl flex flex-col">
                                <div className="relative aspect-video overflow-hidden rounded-2xl">
                                    {article.imageUrl && (
                                        <Image
                                            src={article.imageUrl}
                                            alt={article.title}
                                            fill
                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                            className="object-cover"
                                        />
                                    )}
                                    <div className="absolute top-3 right-3">
                                        <span className={`bg-gradient-to-r ${gradient} text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg ${shadow} hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center gap-2 backdrop-blur-sm`}>
                                            <span className="text-base">{icon}</span>
                                            <span>{article.category}</span>
                                        </span>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-600 mt-1">Temps de lecture : {article.readingTime} min | Publié le : {new Date(article.publishedDate || article.date).toLocaleDateString()}</p>
                                <h3 className="text-xl font-semibold mt-4 break-words">{article.title}</h3>
                                <p className="mt-2">{truncateDescription(article.description)}</p>

                                <div className="mt-auto pt-4 flex justify-center">
                                    <Button shiny onClick={() => window.location.href = `/articles/${article.slug}`}>
                                        Lire...
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default BlogContent;
